import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useLanguageState } from 'contexts/language/language-context';
import dayjs from 'dayjs';

import 'dayjs/locale.json';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/nb';
import ChileLocale from './es-cl';

import { LocalizationProvider } from '@mui/x-date-pickers';
import React, { useContext, useEffect, useState } from 'react';

export type Locale = 'en-gb' | 'nb' | 'es-cl' | 'vn';

type LocaleOptions = {
  [key: string]: string;
};

export const locales: LocaleOptions = {
  en: 'en-gb',
  nb: 'nb',
  es: 'es-cl',
  vn: 'vn',
};

type State = {
  locale: string;
  changeLocale(lang: Locale): void;
};

function getLocale() {
  if (navigator.languages != undefined) return navigator.languages[0];
  return navigator.language || 'en-gb';
}

const SetInitialLocale = () => {
  let initLocale: string = localStorage.getItem('locale') || getLocale();

  let locale = initLocale in locales ? initLocale : 'en-gb';
  dayjs.locale(locale);

  return locale;
};

type LanguageProviderProps = {
  children: React.ReactNode;
};

const LocaleContext = React.createContext<State | undefined>(undefined);

const LocaleProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { language } = useLanguageState();
  const [locale, setLocale] = useState(SetInitialLocale());

  localStorage.setItem('locale', locale);

  const changeLocale = (locale: Locale): void => {
    localStorage.setItem('locale', locale);
    setLocale(locale);
  };

  useEffect(() => {
    let loc = language;

    switch (language) {
      case 'en':
      case 'en-gb':
        loc = 'en-gb';
        break;
      case 'es':
      case 'es-cl':
        loc = 'es-cl';
        break;
      case 'nb':
        loc = 'nb';
        break;
      default:
        loc = 'en-gb';
    }

    setLocale(loc);

    loc === 'es-cl' ? dayjs.locale(ChileLocale) : dayjs.locale(loc); // add custom chile locale object since it does not exist in dayjs
  }, [language]);

  useEffect(() => {
    locale === 'es-cl' ? dayjs.locale(ChileLocale) : dayjs.locale(locale); // add custom chile locale object since it does not exist in dayjs
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, changeLocale }}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
        {children}
      </LocalizationProvider>
    </LocaleContext.Provider>
  );
};

function useLocaleState() {
  const context = useContext(LocaleContext);
  if (context === undefined) {
    throw new Error('useAccountState must be used within a AccountProvider');
  }
  return context;
}

const useOptionalLocaleState = (): State | undefined => useContext(LocaleContext);

export { LocaleProvider, useLocaleState, useOptionalLocaleState };
